import type { CronFields, TimezoneInfo, ValidationResult } from '@/api/definitions';

/**
 * Obtiene información del timezone
 * @param timezone - Timezone string
 * @returns Información del timezone
 */
export function getTimezoneInfo(timezone: string): TimezoneInfo {
  const timezones: Record<string, TimezoneInfo> = {
    'Europe/Madrid': {
      name: 'Europe/Madrid',
      offset: 'GMT+1/+2',
      description: 'Madrid, España (horario peninsular)'
    },
    'UTC': {
      name: 'UTC',
      offset: 'GMT+0',
      description: 'Tiempo Universal Coordinado'
    },
    'Europe/London': {
      name: 'Europe/London',
      offset: 'GMT+0/+1',
      description: 'Londres, Reino Unido'
    },
    'America/New_York': {
      name: 'America/New_York',
      offset: 'GMT-5/-4',
      description: 'Nueva York, EE.UU.'
    },
    'America/Los_Angeles': {
      name: 'America/Los_Angeles',
      offset: 'GMT-8/-7',
      description: 'Los Ángeles, EE.UU.'
    }
  };

  return timezones[timezone] || timezones['Europe/Madrid'];
}

/**
 * Genera una expresión cron basada en la nueva lógica de repetición
 * @param fields - Campos del formulario
 * @returns Expresión cron generada o marcador para lógica personalizada
 */
export function generateCronExpression(fields: CronFields): string {
  const { startDate, hour, day } = fields;

  // Si no hay repetición, solo ejecutar una vez (no es cron)
  if (!hour && !day) {
    return '';
  }

  // Validar que solo uno de los campos esté lleno
  if (hour && day) {
    throw new Error('No se pueden rellenar ambos campos de repetición (hora y día)');
  }

  if (!startDate) {
    return '';
  }

  // Extraer minuto y hora del startDate (como fecha local)
  const startDateTime = new Date(startDate);
  const cronMinute = startDateTime.getMinutes();
  const cronHour = startDateTime.getHours();

  // Repetición por horas
  if (hour) {
    const hourInterval = parseInt(hour.toString(), 10);
    if (hourInterval <= 0 || hourInterval > 23) {
      throw new Error('El intervalo de horas debe estar entre 1 y 23');
    }
    
    // Generar expresión para repetir cada X horas desde la hora de inicio
    return `${cronMinute} */${hourInterval} * * *`;
  }

  // Repetición por días
  if (day) {
    const dayInterval = parseInt(day.toString(), 10);
    if (dayInterval <= 0) {
      throw new Error('El intervalo de días debe ser mayor a 0');
    }

    if (dayInterval === 1) {
      // Cada día a la misma hora
      return `${cronMinute} ${cronHour} * * *`;
    } else {
      return `CUSTOM:EVERY_${dayInterval}_DAYS:${cronMinute}:${cronHour}`;
    }
  }

  return '';
}

/**
 * Valida si una expresión cron es válida (formato básico)
 * @param cronExpression - Expresión cron a validar
 * @returns true si es válida
 */
export function validateCronExpression(cronExpression: string): boolean {
  if (!cronExpression.trim()) {
    return true; // Vacío es válido (sin repetición)
  }
  
  // Aceptar marcadores personalizados para intervalos de días
  if (cronExpression.startsWith('CUSTOM:EVERY_')) {
    return true;
  }
  
  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return false;
  }

  // Validación básica de cada campo cron
  const patterns = [
    /^(\*|[0-5]?\d|\*\/\d+)$/, // minuto (0-59) o */X
    /^(\*|[01]?\d|2[0-3]|\*\/\d+)$/, // hora (0-23) o */X
    /^(\*|[0-2]?\d|3[01]|\*\/\d+)$/, // día (1-31) o */X
    /^(\*|[01]?\d)$/, // mes (1-12)
    /^(\*|[0-6])$/ // día de semana (0-6)
  ];

  return parts.every((part, index) => patterns[index].test(part));
}

/**
 * Convierte una expresión cron a descripción legible
 * @param cronExpression - Expresión cron o marcador personalizado
 * @returns Descripción en texto
 */
export function describeCronExpression(cronExpression: string): string {
  if (!cronExpression.trim()) {
    return 'Ejecución única (sin repetición)';
  }

  // Detectar marcadores personalizados para intervalos de días
  if (cronExpression.startsWith('CUSTOM:EVERY_')) {
    const parts = cronExpression.split(':');
    if (parts.length >= 4) {
      const dayInterval = parts[1].replace('EVERY_', '').replace('_DAYS', '');
      const minute = parts[2];
      const hour = parts[3];
      const dayText = parseInt(dayInterval, 10) > 1 ? 's' : '';
      return `Cada ${dayInterval} día${dayText} a las ${hour.padStart(2, '0')}:${minute.padStart(2, '0')} (lógica personalizada)`;
    }
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return 'Programación personalizada';
  }

  const [minute, hour, day] = parts;

  // Detectar patrones de intervalo por horas
  if (hour && hour.startsWith('*/')) {
    const hourInterval = hour.replace('*/', '');
    const hourText = parseInt(hourInterval, 10) > 1 ? 's' : '';
    return `Cada ${hourInterval} hora${hourText} en el minuto ${minute}`;
  }

  // Patrón diario
  if (hour !== '*' && day === '*') {
    return `Todos los días a las ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }

  return 'Programación personalizada';
}

/**
 * Valida los campos de repetición según las nuevas reglas
 * @param fields - Campos del formulario
 * @returns Objeto con validación y errores
 */
export function validateRepetitionFields(fields: CronFields): ValidationResult {
  const errors: string[] = [];
  const { hour, day, endDate, startDate, timezone } = fields;

  // Validar que no se rellenen ambos campos
  if (hour && day) {
    errors.push('No se pueden rellenar ambos campos de repetición (hora y día)');
  }

  // Si hay repetición, endDate es obligatorio
  if ((hour || day) && !endDate) {
    errors.push('La fecha de fin es obligatoria cuando se especifica repetición');
  }

  // Validar que endDate sea posterior a startDate
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end <= start) {
      errors.push('La fecha de fin debe ser posterior a la fecha de inicio');
    }
  }

  // Validar rangos de hora
  if (hour) {
    const hourNum = parseInt(hour.toString(), 10);
    if (isNaN(hourNum) || hourNum <= 0 || hourNum > 23) {
      errors.push('El intervalo de horas debe estar entre 1 y 23');
    }
  }

  // Validar rangos de día
  if (day) {
    const dayNum = parseInt(day.toString(), 10);
    if (isNaN(dayNum) || dayNum <= 0) {
      errors.push('El intervalo de días debe ser mayor a 0');
    }
  }

  // Validar timezone
  if (timezone) {
    const validTimezones = ['Europe/Madrid', 'UTC', 'Europe/London', 'America/New_York', 'America/Los_Angeles'];
    if (!validTimezones.includes(timezone)) {
      errors.push('Timezone no válido');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}
